/**
 * Lead detail, above the homeowner contact card: "Accept this lead to see the
 * homeowner's details" for a billable partner (contractor pages phase 2).
 *
 * Renders only while the server says the contact is locked
 * (`lead.contact_locked`): the lead shows its job and area, and accepting it
 * (`POST /partner/leads/:id/accept`, owners, admins and operators) unlocks
 * the details and adds the lead fee to the statement. The fee shown is the
 * one the server will post (`accept_fee_cents`). While the Contractor Fee
 * Schedule is not accepted (`fee_schedule_required`) nothing can be
 * accepted; the card says so and links to Billing, where an owner or admin
 * accepts it.
 */
import type { LeadDetail } from '../api/types';
import { can } from '../auth/roles';
import { useSession } from '../auth/session';
import { billingError } from '../billing/errors';
import { formatCents } from '../billing/money';
import { Link } from '../router';
import { ErrorNotice, useToast } from '../ui/kit';
import { useMutation } from '../ui/hooks';

export interface AcceptLeadSlotProps {
  lead: LeadDetail;
  /** Called after a successful accept, so the detail refetches the unlocked contact. */
  onAccepted: () => void;
}

export const FEE_SCHEDULE_LINK = '/billing#fee-schedule';

export function AcceptLeadSlot({ lead, onAccepted }: AcceptLeadSlotProps) {
  if (!lead.contact_locked) return null;
  return <AcceptLeadCard lead={lead} onAccepted={onAccepted} />;
}

function AcceptLeadCard({ lead, onAccepted }: AcceptLeadSlotProps) {
  const { api, role } = useSession();
  const toast = useToast();
  const accept = useMutation(() => api.acceptLead(lead.lead_id));
  const canAccept = can(role, 'quotes');
  const canAcceptSchedule = can(role, 'acceptFeeSchedule');
  const fee = typeof lead.accept_fee_cents === 'number' ? formatCents(lead.accept_fee_cents) : null;
  const scheduleRequired = lead.fee_schedule_required === true || accept.error?.code === 'FEE_SCHEDULE_REQUIRED';
  const descId = `accept-lead-${lead.lead_id}-d`;

  const submit = async () => {
    const res = await accept.run();
    if (!res) return;
    toast('Lead accepted. The homeowner’s details are below.');
    onAccepted();
  };

  return (
    <section className="panel" aria-labelledby={`accept-lead-${lead.lead_id}-h`}>
      <div className="panel-head">
        <h2 id={`accept-lead-${lead.lead_id}-h`}>Accept this lead</h2>
        {fee ? <span className="tag accent">{fee}</span> : null}
      </div>
      <div className="panel-body stack-sm">
        <p id={descId} style={{ margin: 0 }}>
          {fee
            ? `Accept this lead to see the homeowner’s details — ${fee} will be added to your statement. Declining or ignoring it costs nothing.`
            : 'Accept this lead to see the homeowner’s details. Declining or ignoring it costs nothing.'}
        </p>
        {scheduleRequired ? (
          <div className="notice warn small" role="status">
            <span>
              {canAcceptSchedule
                ? 'Before you can accept leads, accept the Contractor Fee Schedule on the Billing page. It sets out the lead fee and the success fee.'
                : 'Before leads can be accepted, an owner or admin needs to accept the Contractor Fee Schedule on the Billing page.'}{' '}
              {can(role, 'billing') ? <Link to={FEE_SCHEDULE_LINK}>{canAcceptSchedule ? 'Review the fee schedule' : 'See Billing'}</Link> : null}
            </span>
          </div>
        ) : null}
        {accept.error?.code === 'FEE_SCHEDULE_REQUIRED' ? null : <ErrorNotice error={billingError(accept.error)} />}
        {canAccept ? (
          scheduleRequired ? null : (
            <div>
              <button type="button" className="btn btn-primary" aria-describedby={descId} disabled={accept.pending} onClick={() => void submit()}>
                {accept.pending ? 'Accepting…' : 'Accept lead'}
              </button>
            </div>
          )
        ) : (
          <p className="small muted" style={{ margin: 0 }}>
            An owner, admin or operator can accept it.
          </p>
        )}
      </div>
    </section>
  );
}
